import { useMemo } from "react"
import { allIntegrations, categories } from "./integrations-data"
import type { Category } from "./integrations-data"

interface IntegrationsListProps {
  searchQuery: string
}

/**
 * Integrations list section with category grouping and search filtering.
 */
export function IntegrationsList({ searchQuery }: IntegrationsListProps) {
  const filteredCategories = useMemo(() => {
    const query = searchQuery.toLowerCase().trim()

    if (!query) {
      // Group all integrations by category
      return categories.map((category) => ({
        name: category,
        slug: category.toLowerCase().replace(/\s+/g, "-"),
        integrations: allIntegrations.filter((i) => i.category === category),
      }))
    }

    // Filter integrations matching search query
    const filtered = allIntegrations.filter(
      (i) =>
        i.name.toLowerCase().includes(query) ||
        i.description.toLowerCase().includes(query) ||
        i.category.toLowerCase().includes(query)
    )

    // Group filtered integrations by category
    const groupedCategories: Category[] = []
    categories.forEach((category) => {
      const categoryIntegrations = filtered.filter((i) => i.category === category)
      if (categoryIntegrations.length > 0) {
        groupedCategories.push({
          name: category,
          slug: category.toLowerCase().replace(/\s+/g, "-"),
          integrations: categoryIntegrations,
        })
      }
    })

    return groupedCategories
  }, [searchQuery])

  const totalResults = useMemo(
    () => filteredCategories.reduce((acc, cat) => acc + cat.integrations.length, 0),
    [filteredCategories]
  )

  if (searchQuery && totalResults === 0) {
    return (
      <section className="bg-neutral-50 py-16">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-lg text-neutral-600">
            No integrations found for &quot;{searchQuery}&quot;
          </p>
          <p className="mt-2 text-neutral-500">
            Can&apos;t find what you&apos;re looking for?{" "}
            <a href="/contact" className="text-primary hover:underline">
              Contact us
            </a>{" "}
            and we&apos;ll add it.
          </p>
        </div>
      </section>
    )
  }

  return (
    <section className="bg-neutral-50 py-16">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        {searchQuery && (
          <p className="mb-8 text-sm text-neutral-500">
            Showing {totalResults} integration{totalResults !== 1 ? "s" : ""} matching &quot;{searchQuery}&quot;
          </p>
        )}

        <div className="space-y-12">
          {filteredCategories.map((category) => (
            <div key={category.slug}>
              {/* Category Header */}
              <h2 className="mb-6 text-xl font-semibold text-neutral-900">
                {category.name}
                <span className="ml-2 text-sm font-normal text-neutral-500">
                  ({category.integrations.length})
                </span>
              </h2>

              {/* Integrations Grid */}
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
                {category.integrations.map((integration) => (
                  <div
                    key={integration.slug}
                    className="flex flex-col items-center gap-3 rounded-xl border border-neutral-200 bg-white p-4 transition-all hover:border-primary/30 hover:shadow-md"
                  >
                    <div
                      className="flex h-12 w-12 items-center justify-center rounded-lg"
                      style={{ backgroundColor: `${integration.color}20` }}
                    >
                      <div
                        className="h-6 w-6"
                        style={{
                          backgroundColor: integration.color,
                          maskImage: `url(${integration.logo})`,
                          maskSize: "contain",
                          maskRepeat: "no-repeat",
                          maskPosition: "center",
                          WebkitMaskImage: `url(${integration.logo})`,
                          WebkitMaskSize: "contain",
                          WebkitMaskRepeat: "no-repeat",
                          WebkitMaskPosition: "center",
                        }}
                      />
                    </div>
                    <div className="text-center">
                      <span className="text-sm font-medium text-neutral-700">
                        {integration.name}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
